import { Box, Pagination, Typography } from "@mui/material";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import pillarsService from "services/pillars-service";
import useOpen from "utils/useOpen";
import CreatePillarDialog from "./CreatePillarDialog.v3";
import MDBadge from "#components/MDBadge/index.js";
import kpisService from "services/kpis-service";
import useObjectState from "utils/useObjectState";
import categoriesService from "services/categories-service";
import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";
import { useAuth } from "contexts/auth.context";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const ALL_PILLARS_TAKE = 100000;

export default function AllPillars({ permissions }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pillars, setPillars] = useState();
  const [count, setCount] = useState(0);
  const [query, setQuery] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [options, changeOptions] = useObjectState({
    cateogries: [],
    valueTypes: [],
  });
  const { label } = useLanguage();
  const open = useOpen();
  const can = usePermissionMonitor();
  const { user } = useAuth();
  const createDialog = open.get("create");
  const editDialog = open.get("edit");

  const createDialogData = useMemo(
    () => ({
      categories: options.categories,
      subcategoryId: createDialog?.currentPillar?.subcategoryId,
      subcategories: createDialog?.currentPillar?.subcategories,
      parentId: createDialog?.currentPillar?.id,
      pillarId: createDialog?.currentPillar?.id,
      valueTypeId: null,
      valueTypes: options.valueTypes,
      parent: createDialog?.currentPillar,
    }),
    [createDialog, options.categories, options.valueTypes],
  );

  const editDialogData = useMemo(
    () => ({
      categories: options.categories,
      subcategoryId: editDialog?.currentPillar?.subcategoryId,
      subcategories: editDialog?.currentPillar?.subcategories,
      parentId: editDialog?.currentPillar?.parentId,
      pillarId: editDialog?.currentPillar?.pillarId,
      valueTypeId: null,
      valueTypes: options.valueTypes,
      kpi: editDialog?.currentPillar?.isKpi ? editDialog.currentPillar : null,
      pillar: !editDialog?.currentPillar?.isKpi
        ? editDialog?.currentPillar
        : null,
    }),
    [editDialog, options.categories, options.valueTypes],
  );

  async function loadPillars(overrideQuery) {
    const requestQuery = overrideQuery || query;
    const [response, data] = await pillarsService.getPillars(requestQuery);
    if (response.ok) {
      setPillars(data.data);
      setCount(data.count);
    }
    return [response, data];
  }

  function handlePageSizeChange(newPageSize) {
    setPageSize(newPageSize);
    setSearchParams((curr) => {
      curr.set("page", 1);
      return curr;
    });
    loadPillars({
      ...query,
      take: newPageSize === "all" ? ALL_PILLARS_TAKE : newPageSize,
      skip: 0,
    });
  }

  const columns = [
    {
      Header: label("description-singular"),
      accessor: "description",
      width: "100%",
      firstField: true,
      align: "left",
      width: "400px",

      bodyCellStyle: {
        minWidth: "400px",
        whiteSpace: "unset",
        overflow: "unset",
        textOverflow: "unset",
      },
    },
    {
      Header: label("account-singular"),
      accessor: "account",
      width: 150,
    },
    {
      Header: label("weight-singular"),
      accessor: "weight",
      width: 120,
    },
    {
      Header: label("reference-code-singular"),
      accessor: "referenceCode",
      align: "center",
      width: 150,
    },
    {
      Header: label("category-plural"),
      accessor: "category",
      align: "center",
      width: "100%",
      minWidth: "300px",
      bodyCellStyle: {
        whiteSpace: "unset",
        overflow: "unset",
        textOverflow: "unset",
        minWidth: "300px",
      },
    },
  ];

  if (permissions?.create || permissions?.update || permissions?.delete) {
    columns.push({
      Header: "",
      accessor: "actions",
      align: "right",
      width: "100%",
    });
  }

  function onClickUpdatePillar(pillar) {
    open.change("edit", {
      currentPillar: pillar,
    });
  }

  function onClickUpdateKpi(kpi) {
    open.change("edit", {
      currentPillar: { ...kpi, isKpi: true },
    });
  }

  function onClickCreateKpi(pillar) {
    open.change("create", { currentPillar: pillar, createChoice: "kpi" });
  }

  function onClickDeletePillar(pillar) {
    open.change("alert", {
      currentPillar: { ...pillar },
    });
  }

  function onClickDeleteKpi(kpi) {
    open.change("alert", {
      currentPillar: { ...kpi, isKpi: true },
    });
  }

  function canManageAccountItem(accountId) {

    if (!user?.account) return false;

    const acceptableAccountIds = [
      user.account.id,
      ...(user.account.accounts?.map((account) => account.id) || []),
    ];

    return acceptableAccountIds.includes(accountId)? true : can("isAdmin");
  }

  async function handleAddPillarOrKpi(newItem) {
    if (newItem.isPillar) {
      const [response, data] = await pillarsService.createPillar({
        description: newItem.description,
        referenceCode: newItem.referenceCode,
        parentId: open.get("create")?.currentPillar?.id,
        subcategoryId: newItem.subcategoryId,
        subcategories: newItem.subcategories,
        accountId: newItem.accountId,
      });
      if (response.ok) {
        await loadPillars();
      } else {
        return { error: data.error };
      }
    } else {
      const [response, data] = await kpisService.createKpi({
        ...newItem,
      });
      if (response.ok) {
        await loadPillars();
      } else {
        return { error: data.error };
      }
    }

    setLoading(false);

    return { success: true };
  }

  async function handleDeletePillarOrKpi() {
    //setLoading(true);

    if (!open.get("alert")?.currentPillar.isKpi) {
      const [response, data] = await pillarsService.removePillar(
        open.get("alert")?.currentPillar.id
      );
      if (response.ok) {
        await loadPillars();
      } else {
        return { error: data.error };
      }
    } else {
      const [response, data] = await kpisService.removeKpi(
        open.get("alert")?.currentPillar.id
      );
      if (response.ok) {
        await loadPillars();
      } else {
        return { error: data.error };
      }
    }

    return { success: true };
  }

  async function handleEditPillarOrKpi(newItem) {
    if (!open.get("edit")?.currentPillar.isKpi) {
      const [response, data] = await pillarsService.updatePillar(newItem.id, {
        ...newItem,
      });
      if (response.ok) {
        await loadPillars();
      } else {
        return { error: data.error };
      }
    } else {
      const [response, data] = await kpisService.updateKpi(newItem.id, {
        ...newItem,
      });
      if (response.ok) {
        await loadPillars();
      } else {
        return { error: data.error };
      }
    }

    return { success: true };
  }

  async function loadOptions(cancelToken) {
    setLoading(true);
    const requests = [categoriesService.getCategories(null, cancelToken)];

    if (permissions.get) {
      requests.push(kpisService.getValueTypes(null, cancelToken));
    }

    Promise.all(requests).then((values) => {
      setLoading(false);
      var [response, data] = values[0];
      if (response.ok) {
        changeOptions("categories", data);
      } else {
        console.log("Error while fetching categories: " + data.error);
      }

      if (permissions.get) {
        [response, data] = values[1];
        if (response.ok) {
          changeOptions("valueTypes", data);
        } else {
          console.log("Error while fetching kpi value types: " + data.error);
        }
      }
    });
  }

  const rows = useMemo(() => {
    return (
      pillars?.map((pillar) => {
        return {
          id: pillar.id,
          data: pillar,
          description: (
            <MDTypography
              variant="inherit"
              sx={{
                // whiteSpace: "nowrap",
                // overflow: "hidden",
                // textOverflow: "ellipsis",
                whiteSpace: "unset",
                overflow: "unset",
                textOverflow: "unset",
              }}
            >
              <MDBadge
                circular
                badgeContent={label("pillar-singular").toUpperCase()}
                variant="gradient"
                color="info"
                container
              />
              &nbsp;&nbsp;
              {pillar.description}{" "}
              <Typography variant="caption">
                ({pillar?.kpis?.length || 0})
              </Typography>
            </MDTypography>
          ),
          account: pillar.account?.name || "All",
          weight: pillar.weight,
          referenceCode: pillar.referenceCode,
          category: (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {pillar?.subcategories
                .map((v) => v.description.trim())
                .map((v) => (
                  <Box
                    sx={(theme) => ({
                      backgroundColor: theme.palette.info.main,
                      color: "white !important",
                      px: 1,
                      py: 0,
                      m: 0,
                      borderRadius: "4px",
                    })}
                  >
                    {v}
                  </Box>
                )) || "None"}
            </Box>
          ),
          actions: (
            <MDBox display="flex" flexDirection="row" justifyContent="end" alignItems="center">
              {permissions.create && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();

                    onClickCreateKpi(pillar);
                  }}
                >
                  <Icon>add</Icon>
                </IconButton>
              )}
              {canManageAccountItem(pillar.accountId) && permissions.update && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();

                    onClickUpdatePillar(pillar);
                  }}
                >
                  <Icon>edit</Icon>
                </IconButton>
              )}
              {canManageAccountItem(pillar.accountId) && permissions.delete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickDeletePillar(pillar);
                  }}
                >
                  <Icon>delete</Icon>
                </IconButton>
              )}
            </MDBox>
          ),
        };
      }) || []
    );
  }, [pillars]);

  useEffect(() => {
    loadOptions();
  }, []);

  return (
    <>
      <AlertDialog
        open={open.get("alert")}
        setOpen={(v) => open.change("alert", v)}
        title={label("delete-alert-title")}
        content={label("delete-alert-message")}
        onOk={async () => {
          await handleDeletePillarOrKpi();
        }}
        onCancel={() => {}}
      />

      {createDialog ? (
        <CreatePillarDialog
          isOpen={true}
          onClose={() => {
            open.change("create", false);
          }}
          onSubmit={handleAddPillarOrKpi}
          data={createDialogData}
          title="Create"
          type="create"
          createChoice={createDialog?.createChoice}
        />
      ) : null}
      {editDialog ? (
        <CreatePillarDialog
          onClose={() => {
            open.change("edit", false);
          }}
          isOpen={true}
          onSubmit={handleEditPillarOrKpi}
          data={editDialogData}
          title="Edit"
          type="edit"
          createChoice={
            editDialog?.currentPillar?.isKpi ? "kpi" : "pillar"
          }
        />
      ) : null}

      {!loading && (
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    {label("pillar-plural")}{" "}
                    {permissions.get ? `(${count || 0})` : ""}
                  </MDTypography>
                  {
                    /*can("isAdmin") &&*/ permissions.create && (
                      <MDButton
                        onClick={() => {
                          open.change("create", {
                            currentPillar: null,
                            open: Math.random(),
                            createChoice: "pillar",
                          });
                        }}
                      >
                        <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        &nbsp;{label("add-new-pillar-button")}
                      </MDButton>
                    )
                  }
                </MDBox>
                {permissions.get ? (
                  <>
                    <MDBox p={2} mt={2}>
                      <TableSearch
                        id="all-pillars"
                        internalLoading={!Boolean(pillars)}
                        fullWidth
                        setData={setPillars}
                        setCount={setCount}
                        onSearch={async (request, callback) => {
                          const take =
                            pageSize === "all" ? ALL_PILLARS_TAKE : pageSize;
                          const newQuery = {
                            ...request,
                            take,
                            skip: (Number(searchParams.get("page") || 1) - 1) * take,
                          };
                          setQuery(newQuery);
                          callback(await pillarsService.getPillars(newQuery));
                        }}
                        label={label("search-pillars-label")}
                      />
                    </MDBox>
                    <MDBox>
                      <DataTable
                        loading={!Boolean(pillars)}
                        table={{ columns, rows }}
                        isSorted={false}
                        entriesPerPage={{ defaultValue: 500000 }}
                        showTotalEntries={false}
                        noEndBorder
                        // virtualize={false}
                        // rowHeight={52} // optional: base row height
                        // detailHeight={240} // optional: fallback height for expanded rows (auto-measured after render)
                        // overscan={8}
                        // rowProps={{
                        //   sx: (theme) => ({
                        //     backgroundColor: theme.palette.info.main + "11",
                        //   }),
                        // }}
                        includeDetails={(row) => {
                          const pillar = row.original.data;

                          return (
                            <Kpis
                              onClickUpdate={onClickUpdateKpi}
                              onClickDelete={onClickDeleteKpi}
                              key={pillar?.id}
                              pillar={pillar}
                              permissions={permissions}
                              can={can}
                              canManageAccountItem={canManageAccountItem}
                            />
                          );
                        }}
                      ></DataTable>
                    </MDBox>
                    <MDBox
                      p={2}
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Pagination
                        count={Math.max(
                          1,
                          Math.ceil(
                            count / (pageSize === "all" ? ALL_PILLARS_TAKE : pageSize),
                          ),
                        )}
                        color={"info"}
                        variant="outlined"
                        page={Number(searchParams.get("page")) || 1}
                        onChange={(_, newPage) => {
                          setSearchParams((curr) => {
                            curr.set("page", newPage);
                            return curr;
                          });

                          const take =
                            pageSize === "all" ? ALL_PILLARS_TAKE : pageSize;
                          loadPillars({
                            ...query,
                            skip: (Number(newPage || 1) - 1) * take,
                            take,
                          });
                        }}
                      />
                      <MDBox sx={{ minWidth: 220 }}>
                        <MDSelect
                          label={label("showing-label") || "Showing"}
                          value={pageSize}
                          onChange={handlePageSizeChange}
                          items={[
                            ...PAGE_SIZE_OPTIONS.map((size) => ({
                              value: size,
                              render: String(size),
                            })),
                            {
                              value: "all",
                              render: label("all-pillars-label") || "All pillars",
                            },
                          ]}
                        />
                      </MDBox>
                    </MDBox>
                  </>
                ) : (
                  <AccessDenied type="component" />
                )}
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      )}
      {loading && (
        <Box
          sx={{
            width: "100%",
            height: "calc(100vh - 150px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color={"info"} />
        </Box>
      )}
    </>
  );
}

function Kpis({
  pillar,
  permissions,
  onClickUpdate,
  onClickDelete,
  can,
  canManageAccountItem,
}) {
  const { label } = useLanguage();
  const auth = useAuth();
  const user = auth.user;
  const columns = [
    {
      Header: label("description-singular"),
      accessor: "description",
      width: "100%",
      firstField: true,
      bodyCellStyle: {
        whiteSpace: "unset",
        overflow: "unset",
        textOverflow: "unset",
      },
    },
    {
      Header: label("account-singular"),
      accessor: "account",
      width: 150,
    },
    {
      Header: label("weight-singular"),
      accessor: "weight",
      width: 120,
    },
    {
      Header: label("reference-code-singular"),
      accessor: "referenceCode",
      align: "center",
      width: 150,
    },
  ];

  if (permissions?.create || permissions?.update || permissions?.delete) {
    columns.push({
      Header: "",
      accessor: "actions",
      align: "right",
      width: "100%",
    });
  }
  const rows = useMemo(() => {
    return (
      pillar?.kpis?.map((kpi) => {
        return {
          id: kpi.id,
          data: kpi,
          description: (
            <MDTypography
              variant="inherit"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <MDBadge
                circular
                badgeContent={label("kpi-singular").toUpperCase()}
                variant="gradient"
                color="primary"
                container
              />
              &nbsp;&nbsp;
              {kpi.description}
            </MDTypography>
          ),
          account: kpi.account?.name || "All",
          weight: kpi.weight,
          referenceCode: kpi.referenceCode,
          category: "",
          actions: (canManageAccountItem(kpi.accountId) ? (
            <MDBox
              display="flex"
              flexDirection="row"
              justifyContent="end"
              alignItems="center"
            >
              {permissions.update && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickUpdate(kpi);
                  }}
                >
                  <Icon>edit</Icon>
                </IconButton>
              )}
              {permissions.delete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickDelete(kpi);
                  }}
                >
                  <Icon>delete</Icon>
                </IconButton>
              )}
            </MDBox>) : null
          ),
        };
      }) || []
    );
  }, [pillar.kpis]);

  return (
    <DataTable
      entriesPerPage={{ defaultValue: 500000 }}
      loading={!Boolean(pillar?.kpis)}
      table={{ columns, rows }}
      isSorted={false}
      showTotalEntries={false}
      noEndBorder
      // rowProps={{
      //   sx: (theme) => ({
      //     backgroundColor: theme.palette.primary.main + "11",
      //   }),
      // }}
    ></DataTable>
  );
}
